import { QueryClient } from '@tanstack/react-query'

import { ApiError, NetworkError } from './api'

/**
 * Shared TanStack Query client.
 *
 * The retry policy matters: a 401, 403, 404 or 422 will never succeed on a
 * retry, and retrying them just delays the redirect to /login or the inline
 * form error. Only network failures and 5xx are worth another attempt.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry(failureCount, error) {
        if (error instanceof ApiError && error.status < 500) return false
        if (error instanceof NetworkError) return failureCount < 1
        return failureCount < 2
      },
    },
    mutations: {
      retry: false,
    },
  },
})

/**
 * Query keys, centralised so invalidation never has to guess.
 *
 * Everything workspace-scoped is keyed by slug. That is not cosmetic: the same
 * user sees different members, projects and billing in each workspace, and a
 * key that omitted the slug would serve one workspace's data under another's
 * URL for as long as the entry stayed fresh.
 */
export const queryKeys = {
  session: ['session'] as const,
  me: ['me'] as const,

  orgs: ['orgs'] as const,
  org: (slug: string) => ['orgs', slug] as const,
  members: (slug: string) => ['orgs', slug, 'members'] as const,
  // The workspace's Claude connection. Keyed by slug like everything else
  // workspace-scoped: the credential is per workspace, and a key without the
  // slug would show one workspace's connection under another's URL.
  aiCredential: (slug: string) => ['orgs', slug, 'ai'] as const,
  invitations: (slug: string) => ['orgs', slug, 'invitations'] as const,

  invitationInbox: ['invitations', 'inbox'] as const,
  invitationPreview: (token: string) => ['invitations', 'preview', token] as const,

  billingPlans: ['billing', 'plans'] as const,
  billingSummary: (slug: string) => ['billing', slug, 'summary'] as const,

  projects: ['projects'] as const,
  // 'list' keeps the per-workspace list from ever colliding with a single
  // project keyed by uuid under the same prefix.
  projectList: (slug: string) => ['projects', 'list', slug] as const,
  // The org-scoped v2 list, which the server has already narrowed to this
  // workspace. Kept under the org prefix so switching workspace cannot serve
  // the previous one's projects.
  orgProjects: (slug: string) => ['orgs', slug, 'projects'] as const,
  project: (id: string) => ['projects', id] as const,
  sprints: (projectId: string) => ['projects', projectId, 'sprints'] as const,
  tasks: (filters: Record<string, unknown> = {}) => ['tasks', filters] as const,
  task: (id: string) => ['tasks', id] as const,
  notes: (filters: Record<string, unknown> = {}) => ['notes', filters] as const,
  search: (term: string) => ['search', term] as const,
  health: ['health'] as const,

  /**
   * The platform admin console. Deliberately NOT keyed by workspace: these
   * reads span every tenant, and nesting them under the org prefix would let a
   * workspace-scoped invalidation quietly wipe them (or, worse, suggest they
   * were scoped when they are not).
   */
  admin: ['admin'] as const,
  adminOverview: ['admin', 'overview'] as const,
  adminWorkspaces: (search: string, page: number) => ['admin', 'workspaces', search, page] as const,
  adminWorkspace: (slug: string) => ['admin', 'workspaces', 'detail', slug] as const,
  adminUsers: (search: string, page: number) => ['admin', 'users', search, page] as const,
  adminAudit: (page: number) => ['admin', 'audit', page] as const,
}
